import type { z } from 'zod';

const plural = (count: number, word: string) => `${String(count)} ${word}${count === 1 ? '' : 's'}`;

/** Zod's own wording for a pattern (“Invalid string: must match pattern …”) is not for people. */
const isDefaultMessage = (message: string) => /^(Invalid|Too (small|big))\b/.test(message);

const FORMAT_MESSAGES: Record<string, string> = {
  email: 'Enter a valid email address',
  url: 'Enter a full link, e.g. https://example.com',
  uuid: 'Enter a valid id',
  datetime: 'Enter a valid date and time',
  regex: 'Not in the expected format',
};

/** A short, plain-English message for the first problem zod found with a field. */
export function describeIssue(issue: z.core.$ZodIssue): string {
  if (!isDefaultMessage(issue.message)) return issue.message;
  switch (issue.code) {
    case 'too_small':
      if (issue.origin === 'string')
        return `Use at least ${plural(Number(issue.minimum), 'character')}`;
      if (issue.origin === 'array') return `Choose at least ${String(issue.minimum)}`;
      return `Must be ${issue.inclusive ? 'at least' : 'more than'} ${String(issue.minimum)}`;
    case 'too_big':
      if (issue.origin === 'string')
        return `Use at most ${plural(Number(issue.maximum), 'character')}`;
      if (issue.origin === 'array') return `Choose at most ${String(issue.maximum)}`;
      return `Must be ${issue.inclusive ? 'at most' : 'less than'} ${String(issue.maximum)}`;
    case 'invalid_format':
      return FORMAT_MESSAGES[issue.format] ?? FORMAT_MESSAGES.regex ?? '';
    case 'invalid_type':
      return issue.expected === 'int' ? 'Use a whole number' : 'Enter a valid value';
    case 'not_multiple_of':
      return `Must be a multiple of ${String(issue.divisor)}`;
    case 'invalid_value':
      return 'Choose one of the listed values';
    default:
      return 'Enter a valid value';
  }
}
